"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowLeft, ExternalLink, Download, Play, Pause, ChevronLeft, ChevronRight } from "lucide-react"
import { useRouter } from "next/navigation"
import { useLanguage } from "@/app/contexts/language-context"

export default function ArchitecturalVisualizationPage() {
  const router = useRouter()
  const { t, language } = useLanguage()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [elevation, setElevation] = useState(120.4)

  const latchTransition = { type: "spring" as const, stiffness: 400, damping: 30, restDelta: 0.001 }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setElevation(prev => prev + (Math.random() > 0.5 ? 2.5 : -2.5))
    }, 800)
    return () => clearInterval(interval)
  }, [])

  const projectImages = [
    {
      src: "/projects/architectural-visualization/screenshots/arch-viz-hero.webp",
      alt: "Exterior evening view of residential complex",
      title: "Exterior Evening View",
      description: "Main entrance and facade during golden hour with warm lighting",
    },
    {
      src: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/554B2D70-1BA5-4BE0-86EC-5978C968455E-plcdbMCJD3ilMcrR7bSPCLH8htRfYK.png",
      alt: "Exterior night view with lighting",
      title: "Exterior Night View",
      description: "Evening ambiance with architectural lighting and illuminated windows",
    },
    {
      src: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/5BA39B64-142D-43B6-8B4B-56FEF42D3EB4-Tz2SiAEocfcZOvhEWACOiHnyylSF6S.png",
      alt: "Interior living room render",
      title: "Interior Living Room",
      description: "Modern living space with natural lighting and contemporary furniture",
    },
    {
      src: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/9B546C63-B7E8-48A6-865E-CA49229D6A86-eVFLerRMWIklAzlQ0ESWlKn89k7tkL.png",
      alt: "Interior kitchen render",
      title: "Interior Kitchen",
      description: "Contemporary kitchen design with wood cabinets and island",
    },
    {
      src: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/85405E7B-F662-4FBB-89DB-4FE7DA769AAE-cgmbJAboDrLEikYae0zT6W6Vtodn9k.png",
      alt: "Aerial view of the complex",
      title: "Aerial Overview",
      description: "Bird's eye view of the entire residential complex with parking and landscaping",
    },
    {
      src: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/9692AAAC-C934-4BEF-92EE-53D9ACFB4486.png-jSTmiW994FLStoZPYM8FTY8DxbVdca.jpeg",
      alt: "Courtyard and landscaping",
      title: "Courtyard Design",
      description: "Central courtyard with playground, landscaping and pedestrian pathways",
    },
  ]

  const processSteps = [
    {
      step: "01",
      title: "Concept & Planning",
      description:
        "Initial client consultation, requirement analysis, and concept development based on architectural drawings.",
      tools: ["AutoCAD", "SketchUp"],
    },
    {
      step: "02",
      title: "3D Modeling",
      description:
        "Detailed 3D modeling of the residential complex including all architectural elements and interior spaces.",
      tools: ["3ds Max", "Blender"],
    },
    {
      step: "03",
      title: "Materials & Texturing",
      description:
        "Application of realistic materials, textures, and surface properties to achieve photorealistic quality.",
      tools: ["Substance Painter", "Photoshop"],
    },
    {
      step: "04",
      title: "Lighting Setup",
      description:
        "Professional lighting setup including natural sunlight, artificial lighting, and atmospheric effects.",
      tools: ["V-Ray", "Corona Renderer"],
    },
    {
      step: "05",
      title: "Rendering",
      description:
        "High-quality rendering with advanced global illumination and ray tracing for photorealistic results.",
      tools: ["V-Ray", "3ds Max"],
    },
    {
      step: "06",
      title: "Post-Production",
      description: "Final image enhancement, color correction, and composition adjustments for presentation quality.",
      tools: ["Photoshop", "After Effects"],
    },
  ]

  const projectSpecs = {
    client: "Residential Development Corp",
    duration: "6 weeks",
    deliverables: "24 High-res renders",
    resolution: "4K (3840x2160)",
    formats: "JPEG, PNG, TIFF",
    scope: "Exterior & Interior Visualization",
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % projectImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length)
  }

  const toggleSlideshow = () => {
    setIsPlaying(!isPlaying)
  }

  // Auto-play slideshow
  useEffect(() => {
    let slideshowInterval: NodeJS.Timeout | null = null
    if (isPlaying) {
      slideshowInterval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % projectImages.length)
      }, 5000) // Change image every 5 seconds
    }
    return () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval)
      }
    }
  }, [isPlaying, projectImages.length])

  return (
    <>
  <main className="main-content pb-16">
        {/* Hero Section */}
        <div className="relative h-[70vh] min-h-[500px] w-full bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 opacity-50">
            <img
              src={projectImages[currentImageIndex].src || "/placeholder.svg"}
              alt={projectImages[currentImageIndex].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Topographic Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-10"></div>

          {/* HUD: Elevation Marker */}
          <div className="absolute bottom-8 right-8 z-20 text-right bg-slate-950/80 p-3 border border-emerald-500/30 backdrop-blur-sm pointer-events-none">
            <span className="text-[10px] font-mono text-emerald-500/70 uppercase tracking-widest block mb-1">Elevation_Z_Axis</span>
            <motion.div
              key={elevation}
              initial={{ y: 5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={latchTransition}
              className="text-lg font-mono text-emerald-400"
            >
              +{elevation.toFixed(2)}m
            </motion.div>
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

          {/* Navigation Controls */}
          <div className="absolute inset-0 flex items-center justify-between px-6 z-10">
            <Button
              variant="ghost"
              size="icon"
              className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
              onClick={prevImage}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
              onClick={nextImage}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Content */}
          <div className="container mx-auto px-6 relative z-10 h-full flex flex-col justify-end pb-12">
            <Button
              variant="ghost"
              size="sm"
              className="text-white mb-6 w-fit hover:bg-white/20"
              onClick={() => router.back()}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('projects.detail.back')}
            </Button>

            <Badge className="mb-4 bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 w-fit">
              {t('projects.architectural-visualization.badge') || '3D Architectural Visualization'}
            </Badge>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl">
              {t('projects.architectural-visualization.title') || 'Architectural Visualization Projects'}
            </h1>

            <p className="text-xl text-white/80 mb-6 max-w-3xl">
              {t('projects.architectural-visualization.description') || 'High-quality photorealistic renders and visualization services for architectural projects'}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-white/80 mb-6">
              <span>Client: {projectSpecs.client}</span>
              <span>•</span>
              <span>Duration: {projectSpecs.duration}</span>
              <span>•</span>
              <span>{projectSpecs.deliverables}</span>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/30 hover:bg-white/20 bg-transparent"
                onClick={toggleSlideshow}
              >
                {isPlaying ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
                {isPlaying ? t('projects.detail.pauseSlideshow') : t('projects.detail.playSlideshow')} {t('projects.detail.slideshow')}
              </Button>

              <div className="text-sm text-white/60">
                {currentImageIndex + 1} / {projectImages.length}
              </div>
            </div>
          </div>

          {/* Image Indicators */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
            {projectImages.map((_, index) => (
              <button
                key={index}
                aria-label={`Go to image ${index + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex ? "bg-white" : "bg-white/40"
                }`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </div>

        {/* Project Details */}
        <div className="container mx-auto px-6 py-16">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-12">
              <TabsTrigger value="overview">{t('projects.detail.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="gallery">{t('projects.detail.tabs.gallery')}</TabsTrigger>
              <TabsTrigger value="process">{t('projects.detail.tabs.process')}</TabsTrigger>
              <TabsTrigger value="specs">{t('projects.detail.tabs.specs')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold mb-6">{t('projects.detail.overview')}</h2>
                  <div className="prose prose-lg dark:prose-invert">
                    <p>{t('projects.architectural-visualization.overview.p1')}</p>
                    <p>{t('projects.architectural-visualization.overview.p2')}</p>
                    <p>{t('projects.architectural-visualization.overview.p3')}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">{t('projects.detail.keyFeatures')}</h3>
                  <div className="space-y-4">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <h4 className="font-semibold">{t(`projects.architectural-visualization.features.${i}.title`) || ['Calidad fotorrealista','Múltiples Perspectivas','Escenarios de Iluminación','Precisión de Materiales'][i-1]}</h4>
                          <p className="text-medium-contrast">{t(`projects.architectural-visualization.features.${i}.description`) || ['Técnicas de renderizado avanzadas para resultados realistas','Vistas exteriores, interiores y aéreas','Condiciones de día y noche','Representación realista de materiales y texturas'][i-1]}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold mb-4">Technology Stack</h3>
                    <div className="flex flex-wrap gap-2">
                      {["Blender", "3ds Max", "V-Ray", "Photoshop", "Corona Renderer", "Substance Painter"].map(
                        (tech) => (
                          <Badge key={tech} variant="outline" className="text-sm">
                            {tech}
                          </Badge>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="gallery" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.gallery')}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectImages.map((image, index) => (
                  <Card key={index} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                    <div className="relative overflow-hidden">
                      <img
                        src={image.src || "/placeholder.svg"}
                        alt={image.alt}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
                          onClick={() => window.open(image.src, "_blank")} // Open image in new tab
                        >
                          <ExternalLink className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{t(`projects.architectural-visualization.gallery.${index}.title`) || image.title}</h3>
                      <p className="text-sm text-medium-contrast">{t(`projects.architectural-visualization.gallery.${index}.description`) || image.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="process" className="space-y-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t('projects.detail.process')}</h2>
                <p className="text-xl text-medium-contrast max-w-3xl mx-auto">
                  {t('projects.architectural-visualization.processIntro') || 'Modeling, texturing, lighting, and post-processing to create realistic renders that match design intent.'}
                </p>
              </div>

              <div className="space-y-8">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex gap-8 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-3">{t(`projects.architectural-visualization.process.${index+1}.title`) || step.title}</h3>
                      <p className="text-medium-contrast mb-4 text-lg">{t(`projects.architectural-visualization.process.${index+1}.description`) || step.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {step.tools.map((tool) => (
                          <Badge key={tool} variant="outline" className="text-sm">
                            {tool}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="specs" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.specs')}</h2>

              <div className="grid md:grid-cols-2 gap-8">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">Project Details</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Client:</span>
                        <span className="font-medium">{projectSpecs.client}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Duration:</span>
                        <span className="font-medium">{projectSpecs.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Deliverables:</span>
                        <span className="font-medium">{projectSpecs.deliverables}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Resolution:</span>
                        <span className="font-medium">{projectSpecs.resolution}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Formats:</span>
                        <span className="font-medium">{projectSpecs.formats}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">Scope:</span>
                        <span className="font-medium">{projectSpecs.scope}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">Technical Specifications</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Rendering Engine</h4>
                        <p className="text-medium-contrast">V-Ray for 3ds Max with Global Illumination</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Lighting</h4>
                        <p className="text-medium-contrast">HDRI environment lighting with artificial light sources</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Materials</h4>
                        <p className="text-medium-contrast">Physically Based Rendering (PBR) materials</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Post-Processing</h4>
                        <p className="text-medium-contrast">Color correction and enhancement in Photoshop</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center mt-12">
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600"
                    onClick={() => window.open("/path/to/your/portfolio.pdf", "_blank")} // Placeholder for download
                  >
                    <Download className="mr-2 h-4 w-4" />
                      {t('projects.detail.viewSource')}
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    onClick={() => window.open("https://example.com/visualization", "_blank")}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t('projects.detail.viewLive')}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-teal-50 to-green-50 dark:from-teal-900/20 dark:to-green-900/20 py-16">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">{t('projects.detail.ctaTitle')}</h2>
            <p className="text-xl text-medium-contrast mb-8 max-w-2xl mx-auto">
              {t('projects.detail.ctaDescription')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600"
                onClick={() => router.push("/#contact")}
              >
                {t('projects.detail.initializeAsset') || 'Initialize Asset Protocol'}
              </Button>
              <Button size="lg" variant="outline" onClick={() => router.push("/#case-studies")}>
                {t('projects.detail.viewMoreProjects') || 'View More Projects'}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
